/* LittleG Tool 01 — "Will it fit?" local, deterministic screening.
 * Checks two axis-aligned floor placements and optional upright straight-door
 * passage. No diagonal tilt, turn geometry, stairs or certified clearance.
 * Never uploads home measurements or modifies commerce/account state.
 */
(function (global) {
  'use strict';

  function dimension(raw) {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 && n <= 10000 ? n : null;
  }

  function allowance(raw) {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 20 ? n : null;
  }

  function classify(gaps, spare) {
    if (gaps.some(gap => gap < -1e-9)) return 'not_fit';
    return gaps.every(gap => gap + 1e-9 >= spare) ? 'clear' : 'tight';
  }

  function assessFit(values) {
    if (!values || typeof values !== 'object' || Array.isArray(values)) return null;
    const baseKeys = ['itemW','itemH','itemD','spaceW','spaceH','spaceD'];
    const base = baseKeys.map(key => dimension(values[key]));
    if (base.some(value => value === null)) return null;
    const spare = allowance(values.margin === undefined ? 2 : values.margin);
    if (spare === null) return null;
    const [itemW,itemH,itemD,spaceW,spaceH,spaceD] = base;
    const orientations = [
      {label:'as_entered',gaps:[spaceW-itemW,spaceH-itemH,spaceD-itemD]},
      {label:'rotated_90',gaps:[spaceW-itemD,spaceH-itemH,spaceD-itemW]}
    ].map(orientation => ({
      ...orientation,
      status:classify(orientation.gaps,spare),
      minGap:Math.min(...orientation.gaps)
    }));
    const feasible=orientations.filter(choice => choice.status !== 'not_fit');
    feasible.sort((a,b)=>b.minGap-a.minGap);
    const best=feasible[0] || [...orientations].sort((a,b)=>b.minGap-a.minGap)[0];
    const space={status:best.status,orientation:best.label,gaps:best.gaps};

    const hasDoorW=values.doorW!=='' && values.doorW!==undefined && values.doorW!==null;
    const hasDoorH=values.doorH!=='' && values.doorH!==undefined && values.doorH!==null;
    let door={status:'not_checked',gaps:null,preferredSide:null};
    if(hasDoorW !== hasDoorH) {
      door={status:'incomplete',gaps:null,preferredSide:null};
    } else if(hasDoorW && hasDoorH) {
      const dw=dimension(values.doorW),dh=dimension(values.doorH);
      if(dw === null || dh === null) return null;
      // The narrower of two upright horizontal alignments is the straight-pass
      // cross-section; turning/tilting near or after the door is not evaluated.
      const preferredSide=itemW <= itemD ? 'width' : 'depth';
      const apertureWidth=Math.min(itemW,itemD);
      const gaps=[dw-apertureWidth,dh-itemH];
      door={status:classify(gaps,spare),gaps,preferredSide};
    }
    let overall='needs_check';
    if(space.status==='not_fit'||door.status==='not_fit') overall='not_fit';
    else if(space.status==='tight'||door.status==='tight') overall='tight';
    else if(space.status==='clear'&&door.status==='clear') overall='clear';
    return {
      contract:'littleg_fit_precheck_v1',
      unit:'cm', assumed_spare_cm:spare,
      space,door,overall,
      evaluated_orientations:['as_entered','rotated_90'],
      verified_delivery_route:false,
      professional_measurement:false
    };
  }

  if(typeof module!=='undefined' && module.exports) module.exports={assessFit,dimension,allowance};
  if(!global.document) return;

  const localeNumber = n => new Intl.NumberFormat(undefined,{maximumFractionDigits:2}).format(Math.round(n*100)/100);
  global.document.querySelectorAll('[data-littleg-fit-tool]').forEach(form=>{
    const headline=form.querySelector('[data-fit-headline]');
    const detail=form.querySelector('[data-fit-result]');
    const doorResult=form.querySelector('[data-fit-door-result]');
    const container=form.querySelector('[data-fit-result-container]');
    if(!headline || !detail || !doorResult || !container) return;
    const zh=form.dataset.fitLocale !== 'en';
    const fields=[...form.querySelectorAll('[data-fit-field]')];

    function render() {
      const entries=Object.fromEntries(fields.map(field=>[field.dataset.fitField,field.value.trim()]));
      const baseKeys=['itemW','itemH','itemD','spaceW','spaceH','spaceD'];
      const empty=baseKeys.filter(key=>entries[key]==='');
      doorResult.hidden=true;
      doorResult.textContent='';
      if(empty.length) {
        container.dataset.state='idle';
        headline.textContent=zh?'輸入六項尺寸，立即查看結果。':'Enter six dimensions to check the fit.';
        detail.textContent=zh?'尚欠 '+empty.length+' 項必需尺寸。':empty.length+' required measurement(s) missing.';
        return;
      }
      const result=assessFit(entries);
      if(!result) {
        container.dataset.state='invalid';
        headline.textContent=zh?'尺寸不正確':'Check your measurements';
        detail.textContent=zh?'請使用大於零的厘米數值；預留空隙必須介乎 0 至 20 cm。':'Use positive centimetre measurements; the spare clearance must be 0–20 cm.';
        return;
      }
      container.dataset.state=result.overall;
      const state=result.space.status;
      headline.textContent=state==='not_fit'
        ? (zh?'兩種直角擺法都未能放入':'Neither checked right-angle placement fits')
        : state==='tight'
          ? (zh?'擺放尺寸接近，建議再核量':'Very close fit — remeasure first')
          : (zh?'擺放尺寸初步足夠':'The placement dimensions appear sufficient');
      const orientation=result.space.orientation==='rotated_90'
        ? (zh?'轉向 90°':'90° floor rotation')
        : (zh?'原有方向':'Original orientation');
      detail.textContent=(zh?'較合適方向：':'Best checked direction: ')+orientation+
        (zh?'。闊／高／深剩餘：' : '. Remaining width / height / depth: ')+
        result.space.gaps.map(localeNumber).join(' / ')+' cm。'+
        (zh?'這只核對長方體的直角擺放。':' This checks rectangular placement only.');
      if(result.door.status!=='not_checked') {
        doorResult.hidden=false;
        if(result.door.status==='incomplete') doorResult.textContent=
          zh?'門口檢查尚欠淨闊或淨高；請填寫兩項。':'Doorway check needs both clear width and height.';
        else {
          const state=result.door.status;
          const label=state==='not_fit'
            ? (zh?'門口直立直行預檢不通過':'Upright straight-door check does not fit')
            : state==='tight'
              ? (zh?'門口空隙較少，建議核量':'Doorway has little spare clearance')
              : (zh?'門口直立直行尺寸初步足夠':'Upright straight-door dimensions appear sufficient');
          doorResult.textContent=label+
            (zh?'；淨闊／淨高剩餘：':' — remaining clear width / height: ')+
            result.door.gaps.map(localeNumber).join(' / ')+' cm。';
        }
      }
    }
    form.addEventListener('input',render);
    form.addEventListener('change',render);
    form.addEventListener('submit',event=>{event.preventDefault();render();});
    render();
  });
})(typeof window==='undefined' ? globalThis : window);
